import { ReactElement, useCallback } from 'react';

import { VisitAndAppointmentView } from '@dreryk/egabinet-visit-frontend-api';

import { TranslatableText, Translate } from '@dreryk/react-common';
import { Dropdown } from '@dreryk/react-components';

import { useResourceBranchByHealthcareProviderType } from '~libs.shared.account';

import { appointmentsTranslationKeys } from '../../../../translations';
import { RefetchAppointments } from '../../../appointments.types';
import { useVisitCancelCommand } from '../useVisitCancelCommand';

const useVisitCancelOptionTranslationKey = (reopened: boolean, chronicDiseaseExists: boolean): TranslatableText => {
   const genericCancelTranslationKey = useResourceBranchByHealthcareProviderType(
      appointmentsTranslationKeys.cancellingIsNotPossibleInOrganization,
      appointmentsTranslationKeys.cancellingIsNotPossibleInPractice
   );
   if (reopened) {
      return appointmentsTranslationKeys.cannotCancelReopenedVisit;
   }

   if (chronicDiseaseExists) {
      return appointmentsTranslationKeys.cannotCancelVisitWhenChronicDiseaseExists;
   }

   return genericCancelTranslationKey;
};

type VisitCancelOptionProps = {
   readonly visitId: VisitAndAppointmentView['visitId'];
   readonly enabled: boolean;
   readonly reopened: boolean;
   readonly chronicDiseaseExists: boolean;
   readonly refetchAppointments: RefetchAppointments;
   readonly label: TranslatableText;
};

export const VisitCancelDropdownItem = (props: VisitCancelOptionProps): ReactElement => {
   const { executeAsync: cancelVisit } = useVisitCancelCommand({
      onSuccess: props.refetchAppointments,
      onFault: props.refetchAppointments,
   });
   const tooltipTranslationKey = useVisitCancelOptionTranslationKey(props.reopened, props.chronicDiseaseExists);

   const handleClick = useCallback(
      (hide) => {
         cancelVisit({
            visitId: props.visitId!,
         });
         hide();
      },
      [cancelVisit, props.visitId]
   );

   return (
      <Dropdown.Item
         key={'visitCancel'}
         testId={`visitCancelOption${props.visitId}`}
         onClick={handleClick}
         disabled={!props.enabled}
      >
         <Dropdown.Item.Tooltip
            testId={'cancellingIsNotPossibleIfDocumentsWereAdded'}
            content={tooltipTranslationKey}
            disabled={props.enabled}
         >
            <Translate>{props.label}</Translate>
         </Dropdown.Item.Tooltip>
      </Dropdown.Item>
   );
};
